import { faArrowTurnDownRight } from '@fortawesome/pro-solid-svg-icons'
import { Flex, Text, Tooltip } from '@mantine/core'
import { useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import {
  type CoreBundleItem,
  type CoreBundle,
  type CoreItem,
} from '@flecto/types'
import {
  AvatarItem,
  BadgeItemStatus,
  BadgeItemStatusStore,
  Icon,
  TextCurrency,
  TextItemCategory,
  type DataTableColumn,
} from '@flecto/ui'

import { TextBundleDiscount } from '../TextBundleDiscount/TextBundleDiscount'
import { TextItemPrice } from '../TextItemPrice/TextItemPrice'

const getItem = (row: CoreBundle | CoreBundleItem): CoreBundle | CoreItem =>
  'item_type' in row && row.item_type === 'bundle'
    ? row
    : (row as CoreBundleItem).item

const renderAvatarTitle = (row: CoreBundle | CoreBundleItem) => {
  const item = 'title' in row ? row : 'item' in row ? row.item : null
  if (!item) return null
  return (
    <Flex style={{ overflow: 'hidden' }} gap='md' align='center'>
      <AvatarItem item={row} />
      <Tooltip label={item.title}>
        <Text fw={500} truncate>
          {item.title}
        </Text>
      </Tooltip>
    </Flex>
  )
}

export const useColumns = (): DataTableColumn<
  CoreBundle | CoreBundleItem
>[] => {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })

  return useMemo(
    () => [
      {
        accessor: 'id',
        title: 'ID',
        width: 70,
        render: (row) =>
          'internal_id' in row && row.internal_id ? (
            <Text c='dimmed'>#{row.internal_id}</Text>
          ) : (
            <Icon color='gray.6' icon={faArrowTurnDownRight} />
          ),
      },
      {
        accessor: 'title',
        title: t('label_bundle'),
        width: 250,
        render: renderAvatarTitle,
      },
      {
        accessor: 'status',
        title: t('label_status'),
        width: 120,
        render: (row) => <BadgeItemStatus item={getItem(row)} />,
      },
      {
        accessor: 'category',
        title: t('label_category'),
        width: 280,
        render: (row) => {
          const item = getItem(row)
          const text = (
            <TextItemCategory
              c='dimmed'
              categoryId={item.category_id ?? ''}
              withBreadcrumbNames
              truncate
            />
          )
          return (
            <Tooltip label={text}>
              <Text c='dimmed' truncate>
                {text}
              </Text>
            </Tooltip>
          )
        },
      },
      {
        accessor: 'quantity',
        title: t('label_total_quantity'),
        width: 80,
        render: (row) => {
          if ('bundle_items' in row && row.bundle_items)
            return row.bundle_items.reduce(
              (sum, item) => sum + (item.quantity || 0),
              0
            )
          if ('item' in row) return row.item.quantity ? row.quantity : '∞'
          return undefined
        },
      },
      {
        accessor: 'price',
        title: t('label_price'),
        width: 120,
        render: (row) => {
          if ('item_type' in row && row.item_type === 'bundle') return '-'
          if ('item' in row && row.item)
            return <TextItemPrice item={row.item} />
          return undefined
        },
      },
      {
        accessor: 'discount',
        title: t('label_discount'),
        width: 80,
        render: (row) => <TextBundleDiscount row={row} />,
      },
      {
        accessor: 'deposit',
        title: t('label_deposit'),
        width: 80,
        render: (row) =>
          'item' in row && row.item ? (
            <TextCurrency cents={row.item.deposit_amount} />
          ) : (
            '-'
          ),
      },
      {
        accessor: 'on_store',
        title: t('label_on_store'),
        width: 120,
        render: (row) => <BadgeItemStatusStore item={getItem(row)} />,
      },
    ],
    [t]
  )
}
